import React, { forwardRef, useCallback, useId, useImperativeHandle, useRef } from 'react';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { Icon } from '@/modules/Icon';
import { InputSize } from '@/shared/types/types';
import { InputRef } from '@/shared/types/input';
import { cn } from '@/shared/utils/cn';
import { useInputSlots } from '@/modules/Input/hooks/useInputSlots';
import { useInputStyles } from '@/modules/Input/hooks/useInputStyles';
import { useDateTimeInput } from '../hooks/useDateTimeInput';
import {
  sanitizeNumericSegment,
  sanitizeYearSegment,
  INLINE_SEGMENT_WIDTH,
  type DateTimeParts,
} from '../utils/datetimeFormat';
import { DateTimeSeparator, SegmentField } from './DateTimeSegmentFields';

export interface DateTimeSegmentInputProps {
  value: Date | null;
  placeholder?: string;
  isOpen: boolean;
  onInputClick: () => void;
  onDateChange?: (date: Date | null) => void;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  className?: string;
  disabled?: boolean;
  label?: string;
  size?: InputSize;
  showSeconds?: boolean;
}

/**
 * Input compuesto con fecha y hora en segmentos numéricos dentro del campo (variante datetime).
 *
 * Soporta edición por segmentos (día, mes, año, hora, minuto, segundo, AM/PM) y cadena
 * datetime completa (doble clic o pegado).
 *
 * @example
 * ```tsx
 * <DateTimeSegmentInput
 *   value={selectedDate}
 *   isOpen={false}
 *   onInputClick={openCalendar}
 *   onDateChange={setSelectedDate}
 * />
 * ```
 */
export const DateTimeSegmentInput = forwardRef<InputRef, DateTimeSegmentInputProps>(
  (
    {
      value,
      placeholder = 'dd/MM/yyyy hh:mm:ss AM',
      isOpen,
      onInputClick,
      onDateChange,
      onInputFocus,
      onInputBlur,
      className = '',
      disabled = false,
      label = '',
      size = 'sm',
      showSeconds = true,
    },
    ref
  ) => {
    const uniqueId = useId();
    const containerRef = useRef<HTMLDivElement>(null);
    const rootInputRef = useRef<HTMLInputElement>(null);
    const dayRef = useRef<HTMLInputElement>(null);

    const {
      parts,
      editMode,
      fullStringValue,
      updatePart,
      commitPart,
      handlePaste,
      enterFullStringMode,
      commitFullString,
      toggleMeridiem,
      setFullStringValue,
      setEditMode,
    } = useDateTimeInput({
      value,
      showSeconds,
      onChange: onDateChange,
    });

    const { paddingClasses } = useInputSlots({
      size,
      extraSlot: faCalendar,
    });

    const { baseClasses, containerClasses, labelClasses } = useInputStyles({
      size,
      disabled,
      fullWidth: true,
      paddingClasses,
      pill: 'sm',
    });

    useImperativeHandle(
      ref,
      () => ({
        getValue: () => fullStringValue,
        setValue: () => undefined,
        focus: () => dayRef.current?.focus(),
        blur: () => rootInputRef.current?.blur(),
        clear: () => onDateChange?.(null),
        getInputElement: () => containerRef.current as HTMLInputElement | null,
      }),
      [fullStringValue, onDateChange]
    );

    const handleRootPaste = useCallback(
      (event: React.ClipboardEvent) => {
        const text = event.clipboardData.getData('text');
        if (text) {
          event.preventDefault();
          handlePaste(text);
        }
      },
      [handlePaste]
    );

    const renderSegments = (segmentParts: DateTimeParts) => (
      <div
        className="ui-flex ui-min-w-0 ui-flex-1 ui-items-center ui-gap-2"
        onDoubleClick={(event) => {
          event.preventDefault();
          enterFullStringMode();
        }}
      >
        <SegmentField
          value={segmentParts.day}
          placeholder="dd"
          maxLength={2}
          widthClass={INLINE_SEGMENT_WIDTH.twoDigit}
          disabled={disabled}
          ariaLabel="Día"
          inputRef={dayRef}
          onFocus={onInputFocus}
          onChange={(v) => updatePart('day', sanitizeNumericSegment(v))}
          onCommit={() => commitPart('day')}
        />
        <DateTimeSeparator>/</DateTimeSeparator>
        <SegmentField
          value={segmentParts.month}
          placeholder="MM"
          maxLength={2}
          widthClass={INLINE_SEGMENT_WIDTH.twoDigit}
          disabled={disabled}
          ariaLabel="Mes"
          onChange={(v) => updatePart('month', sanitizeNumericSegment(v))}
          onCommit={() => commitPart('month')}
        />
        <DateTimeSeparator>/</DateTimeSeparator>
        <SegmentField
          value={segmentParts.year}
          placeholder="yyyy"
          maxLength={4}
          widthClass={INLINE_SEGMENT_WIDTH.fourDigit}
          disabled={disabled}
          ariaLabel="Año"
          onChange={(v) => updatePart('year', sanitizeYearSegment(v))}
          onCommit={() => commitPart('year')}
        />

        <span className="ui-mx-16 ui-select-none ui-text-tertiary" aria-hidden>
          {' '}
        </span>

        <SegmentField
          value={segmentParts.hour12}
          placeholder="hh"
          maxLength={2}
          widthClass={INLINE_SEGMENT_WIDTH.twoDigit}
          disabled={disabled}
          ariaLabel="Hora (01-12)"
          onChange={(v) => updatePart('hour12', sanitizeNumericSegment(v))}
          onCommit={() => commitPart('hour12')}
        />
        <DateTimeSeparator>:</DateTimeSeparator>
        <SegmentField
          value={segmentParts.minute}
          placeholder="mm"
          maxLength={2}
          widthClass={INLINE_SEGMENT_WIDTH.twoDigit}
          disabled={disabled}
          ariaLabel="Minutos (00-59)"
          onChange={(v) => updatePart('minute', sanitizeNumericSegment(v))}
          onCommit={() => commitPart('minute')}
        />
        {showSeconds && (
          <>
            <DateTimeSeparator>:</DateTimeSeparator>
            <SegmentField
              value={segmentParts.second}
              placeholder="ss"
              maxLength={2}
              widthClass={INLINE_SEGMENT_WIDTH.twoDigit}
              disabled={disabled}
              ariaLabel="Segundos (00-59)"
              onChange={(v) => updatePart('second', sanitizeNumericSegment(v))}
              onCommit={() => commitPart('second')}
            />
          </>
        )}
        <button
          type="button"
          disabled={disabled}
          aria-label="Alternar AM/PM"
          onClick={toggleMeridiem}
          className={cn(
            'ui-ml-16 ui-rounded-control-xs ui-px-12 ui-py-2',
            'ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs',
            'ui-text-primary hover:ui-bg-surface-brand-default',
            'disabled:ui-cursor-not-allowed disabled:ui-text-disabled'
          )}
        >
          {segmentParts.meridiem}
        </button>
      </div>
    );

    return (
      <div className={cn('ui-imj ui-relative ui-flex ui-w-full ui-flex-col ui-gap-1', className)}>
        {label && (
          <label htmlFor={`datetime-input-${uniqueId}`} className={labelClasses}>
            {label}
          </label>
        )}
        <div ref={containerRef} className={cn(containerClasses, 'group')} onPaste={handleRootPaste}>
          <div
            className={cn(
              baseClasses,
              'ui-flex ui-min-w-0 ui-items-center ui-pr-[3.25rem]',
              editMode === 'fullString' && 'ui-p-0'
            )}
            aria-expanded={isOpen}
          >
            {editMode === 'fullString' ? (
              <input
                ref={rootInputRef}
                id={`datetime-input-${uniqueId}`}
                type="text"
                disabled={disabled}
                value={fullStringValue}
                placeholder={placeholder}
                onFocus={onInputFocus}
                onBlur={() => {
                  commitFullString();
                  onInputBlur?.();
                }}
                onChange={(event) => setFullStringValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    commitFullString();
                  }
                  if (event.key === 'Escape') {
                    setEditMode('segments');
                  }
                }}
                className="ui-h-full ui-w-full ui-min-w-0 ui-border-0 ui-bg-transparent ui-px-12 ui-outline-none"
              />
            ) : (
              renderSegments(parts)
            )}
          </div>

          <div className="ui-pointer-events-none ui-absolute ui-right-2.5 ui-top-1/2 ui-z-[4] ui-flex ui--translate-y-1/2 ui-items-center">
            <button
              type="button"
              onClick={onInputClick}
              disabled={disabled}
              aria-label="Abrir calendario"
              className={cn(
                'ui-pointer-events-auto ui-flex ui-place-items-center ui-transition-colors',
                disabled
                  ? 'ui-cursor-not-allowed ui-text-disabled'
                  : 'ui-text-tertiary hover:ui-text-brand group-focus-within:ui-text-brand'
              )}
            >
              <Icon name={faCalendar} size="sm" color="current" />
            </button>
          </div>
        </div>
      </div>
    );
  }
);

DateTimeSegmentInput.displayName = 'DateTimeSegmentInput';
